import React, { useRef, useState, useCallback } from 'react';
import './selfcare360Individual.css';
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";

import { downloadPdf, openPdf } from '../utils/pdfGenerator';
import { Switch, Typography } from 'antd';

import PageCover from '../pages/pageCover';
import FloatingActionButtons from '../../floatingActionButtons/floatingActionButtons';

import PageWelcomeSelfcare360 from '../pages/selfcare360/pageWelcomeSelfcare360';
import PageWelcome2Selfcare360 from '../pages/selfcare360/pageWelcome2Selfcare360';
import PageTechnicalInfoSelfcare360 from '../pages/selfcare360/pageTechnicalInfoSelfcare360';
import PageResumeSelfcare360_1 from '../pages/selfcare360/resume/pageResumeSelfcare360_1';
import PageResumeSelfcare360_2 from '../pages/selfcare360/resume/pageResumeSelfcare360_2';
import PageResumeSelfcare360_3 from '../pages/selfcare360/resume/pageResumeSelfcare360_3';
import PageResumeGapSelfcare360 from '../pages/selfcare360/resume/pageResumeGapSelfcare360';
import PageResultsByDimensionIndividual from '../pages/pageResultsByDimensionIndividual';
import PageResultsByNpsIndividual from '../pages/pageResultsByNpsIndividual';
import PageTextualAnswersIndividual from '../pages/pageTextualAnswersIndividual';
import PageActionPlanSelfcare360 from '../pages/selfcare360/pageActionPlanSelfcare360';
import PageFarewellSelfcare360 from '../pages/selfcare360/pageFarewellSelfcare360';
import PageInterventionPlanSummarySelfcare360 from '../pages/selfcare360/pageInterventionPlanSummarySelfcare360';
import PageInterventionPlanDimensionSelfcare360 from '../pages/selfcare360/pageInterventionPlanDimensionSelfcare360';
import { SELFCARE360_DIMENSION_THRESHOLDS } from '../components/selfcare360/selfcare360Scales';
import {
    getIpraActionPlanPrefillRows,
    getIpraDetailedPlanRows,
} from '../pages/selfcare360/selfcare360InterventionPlan';

const { Text } = Typography;

const PRESERVE_UPPER_WORDS = new Set([
    'HWI',
    'ISO',
    'BBS',
    'TRIR',
    'IPRA',
    'SLBA',
]);

const cleanWordForAcronymCheck = (word) => {
    if (!word) return '';
    // Mantiene letras/números, elimina símbolos (incluye ®)
    return word.replace(/[^0-9A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g, '');
};

const toTitleCaseWords = (value) => {
    if (!value || typeof value !== 'string') return null;
    const normalized = value.trim().replace(/\s+/g, ' ');
    if (!normalized) return null;

    return normalized
        .split(' ')
        .map((word) => {
            const clean = cleanWordForAcronymCheck(word);
            const upperClean = clean.toUpperCase();

            // Preservar SOLO siglas conocidas (evita casos como POLAR)
            if (PRESERVE_UPPER_WORDS.has(upperClean)) return upperClean;

            // Title Case simple por palabra
            const first = word.charAt(0).toUpperCase();
            const rest = word.slice(1).toLowerCase();
            return `${first}${rest}`;
        })
        .join(' ');
};

const toSentenceCase = (value) => {
    if (!value || typeof value !== 'string') return '';
    const normalized = value.trim().replace(/\s+/g, ' ');
    if (!normalized) return '';

    // Si es una sigla (una sola palabra) y está en lista blanca, la dejamos en mayúsculas
    if (!normalized.includes(' ')) {
        const clean = cleanWordForAcronymCheck(normalized);
        const upperClean = clean.toUpperCase();
        if (PRESERVE_UPPER_WORDS.has(upperClean)) return upperClean;
    }

    const lower = normalized.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const formatCoverDateEs = (dateLike) => {
    if (!dateLike) return null;
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return null;

    const months = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
    ];

    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const Selfcare360Individual = ({ totalResults, individualResult, selectedEvaluation, orgLogo, orgName }) => {
    const pdfRef = useRef();
    const [showNPS, setShowNPS] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);
    const calculateIpraFromBackendWeights = (averageByDimension) => {
        if (!averageByDimension) return null;

        let weightedSum = 0;
        let hasWeightedDimensions = false;

        Object.entries(averageByDimension).forEach(([key, dim]) => {
            if (String(key).toLowerCase().includes('general')) return;

            const score = Number(dim?.average);
            const weight = Number(dim?.weight);
            if (!Number.isFinite(score) || !Number.isFinite(weight)) return;

            hasWeightedDimensions = true;
            weightedSum += score * weight;
        });

        return hasWeightedDimensions ? Math.round(weightedSum * 10) / 10 : null;
    };

    const backendAverage = Number(individualResult?.average);
    const resolvedIpraAverage = Number.isFinite(backendAverage)
        ? backendAverage
        : calculateIpraFromBackendWeights(individualResult?.average_by_dimension);

    const modifiedIndividualResult = {
        ...individualResult,
        average: Number.isFinite(resolvedIpraAverage) ? resolvedIpraAverage : 0
    };
    
    const relsCountTotal = Object.values(individualResult?.relationships_by_type || {}).reduce((a,b) => a+b, 0);

    const orgNameTitleCase = toTitleCaseWords(orgName);

    const getColor = (value) => {
        if (value <= SELFCARE360_DIMENSION_THRESHOLDS.redMax) return "#e53935";
        if (value <= SELFCARE360_DIMENSION_THRESHOLDS.yellowMax) return "#fbc02d";
        return "#43a047";
    };

    const prepareDimensionData = (dimension, key) => {
        const subdimensions = dimension?.subdimensions || [];
        const orgDimension = totalResults?.average_by_dimension?.[key];

        const chartGroups = subdimensions.map((subdimension, index) => {
            const orgSub = orgDimension?.subdimensions?.find((s) => s.id === subdimension.id);

            return {
                id: subdimension.id || index,
                question: subdimension.question || 'Pregunta sin definir',
                label: `Pregunta ${index + 1}`,
                average: subdimension.average ?? 0,
                orgAverage: orgSub?.average ?? 0,
            };
        });

        return {
            key,
            title: dimension?.title || key,
            dimension,
            chartGroups,
            globalAverage: dimension?.average ?? 0,
            orgGlobalAverage: orgDimension?.average ?? 0,
            color: getColor(dimension?.average ?? 0),
            type: 1,
        };
    };

    const preparedDimensions = [
        ...Object.entries(individualResult.average_by_dimension || {}).map(([key, dimension]) => ({
            key,
            ...prepareDimensionData(dimension, key),
        })),
    ];

    const evaluatedDimensions = Object.values(individualResult.average_by_dimension || {})
        .map((d) => d?.title)
        .filter(Boolean);

    const evaluatedDimensionsSentenceCase = evaluatedDimensions.map(toSentenceCase);

    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    };

    const preparedTextualDimensions = (individualResult.general || []).flatMap((dimension, dimIndex) => {
        return (dimension.subdimensions || [])
            .filter((sub) => sub.type === "textual")
            .flatMap((sub, subIndex) => {
                const chunks = chunkArray(sub.text_answers || [], 15);
                return chunks.map((chunk, chunkIndex) => ({
                    key: `${dimIndex}-${sub.id || subIndex}-${chunkIndex}`,
                    title: dimension.title,
                    question: sub.question,
                    answers: chunk,
                }));
            });
    });

    const npsSubdimension = individualResult.general?.[0]?.subdimensions?.[0] || null;
    const npsResponsesCount = npsSubdimension
        ? Object.values(npsSubdimension.counts || {}).reduce((acc, count) => acc + (Number(count) || 0), 0)
        : 0;
    const detailedPlanRows = getIpraDetailedPlanRows(modifiedIndividualResult?.average_by_dimension);
    const actionPlanPrefillRows = getIpraActionPlanPrefillRows(modifiedIndividualResult?.average_by_dimension);

    const pdfFilename = `${selectedEvaluation.evl_title} - Reporte Individual - ${individualResult.first_name} ${individualResult.last_name}.pdf`;
    const onProgress = useCallback((current, total) => setPdfProgress({ current, total }), []);
    const handleDownloadPDF = async () => { setPdfLoading(true); await downloadPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };
    const handleOpenPDF = async () => { setPdfLoading(true); await openPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };

    return (
        <>
            <div className='sticky-switch'>
                <div className='sticky-switch-content'>
                    <Text style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
                        Visualización resultados NPS
                    </Text>
                    <Switch
                        checked={showNPS}
                        onChange={setShowNPS}
                        checkedChildren={<span style={{ fontSize: 14 }}>Visualizar</span>}
                        unCheckedChildren={<span style={{ fontSize: 14 }}>No visualizar</span>}
                    />
                </div>
            </div>

            <div ref={pdfRef} style={{ fontFamily: 'Poppins, sans-serif' }} className="pdf-content">
                {relsCountTotal < 5 && (
                    <div style={{ backgroundColor: '#fff3cd', color: '#f57f17', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px', borderBottom: '2px solid #fbc02d', marginBottom: '15px' }}>
                        Atención: Este informe contiene resultados exploratorios o parciales. El número total de respuestas ({relsCountTotal}) es inferior al mínimo requerido (5) para garantizar la validez estadística y confidencialidad.
                    </div>
                )}
                <PageCover
                    reportTitle="REPORTE INDIVIDUAL"
                    reportSubtitle={selectedEvaluation.evl_title}
                    tagline="Índice Predictivo de Exposición al riesgo"
                    footerLines={[
                        `Fecha: ${formatCoverDateEs(selectedEvaluation?.evl_start_date) || ''}`.trim(),
                        '“Assessment basado en leading indicators- ISO 45001 aligned, BBS y Reason´s Swiss Cheese Model”',
                    ].filter(Boolean)}
                    variant="selfcare360"
                    orgName={`${individualResult.first_name} ${individualResult.last_name}`}
                    orgRole={individualResult.job_title}
                    year={new Date(selectedEvaluation.evl_start_date).getFullYear()}
                    general={false}
                    orgLogo={orgLogo}
                />

                <PageWelcomeSelfcare360 selectedEvaluation={selectedEvaluation} pageNumber={1} orgName={orgNameTitleCase} />
                <PageWelcome2Selfcare360 selectedEvaluation={selectedEvaluation} pageNumber={2} orgName={orgNameTitleCase} />
                <PageTechnicalInfoSelfcare360
                    selectedEvaluation={selectedEvaluation}
                    pageNumber={3}
                    evaluatedDimensions={evaluatedDimensionsSentenceCase}
                />
                <PageResumeSelfcare360_1
                    selectedEvaluation={selectedEvaluation}
                    individualResult={modifiedIndividualResult}
                    orgLogo={orgLogo}
                    pageNumber={4}
                />

                <PageResumeSelfcare360_2
                    selectedEvaluation={selectedEvaluation}
                    totalResults={totalResults}
                    individualResult={modifiedIndividualResult}
                    orgLogo={orgLogo}
                    pageNumber={5}
                />

                <PageResumeSelfcare360_3
                    selectedEvaluation={selectedEvaluation}
                    individualResult={modifiedIndividualResult}
                    orgLogo={orgLogo}
                    pageNumber={6}
                />

                <PageResumeGapSelfcare360
                    selectedEvaluation={selectedEvaluation}
                    individualResult={modifiedIndividualResult}
                    orgLogo={orgLogo}
                    pageNumber={7}
                />

                {(() => {
                    const generatedPages = [];
                    let currentPageNumber = 7;

                    preparedDimensions.forEach((dimData) => {
                        const type = dimData.type;
                        let totalPages = 1;

                        if (type === 1) {
                            totalPages = dimData.dimension.subdimensions.length > 10 ? 3 : 2;
                        }

                        for (let index = 0; index < totalPages; index += 1) {
                            currentPageNumber += 1;
                            generatedPages.push(
                                <PageResultsByDimensionIndividual
                                    key={`${dimData.key}-page-${currentPageNumber}`}
                                    selectedEvaluation={selectedEvaluation}
                                    totalResults={totalResults}
                                    individualResult={modifiedIndividualResult}
                                    orgLogo={orgLogo}
                                    pageNumber={currentPageNumber}
                                    indexPage={index}
                                    {...dimData}
                                    totalQuestions={preparedDimensions.length}
                                />
                            );
                        }
                    });

                    if (npsSubdimension) {
                        currentPageNumber += 1;
                        generatedPages.push(
                            <PageResultsByNpsIndividual
                                key={`nps-${currentPageNumber}`}
                                selectedEvaluation={selectedEvaluation}
                                individualResult={modifiedIndividualResult}
                                orgLogo={orgLogo}
                                pageNumber={currentPageNumber}
                                title={individualResult.general[0].title}
                                subdimension={npsSubdimension}
                                showNPS={showNPS}
                                totalN={npsResponsesCount}
                            />
                        );
                    }

                    preparedTextualDimensions.forEach((dimData, index) => {
                        if (dimData.answers.length > 0) {
                            currentPageNumber += 1;
                            generatedPages.push(
                                <PageTextualAnswersIndividual
                                    key={`TD${dimData.key}-${index}`}
                                    individualResult={modifiedIndividualResult}
                                    selectedEvaluation={selectedEvaluation}
                                    orgLogo={orgLogo}
                                    title={dimData.title}
                                    question={`${dimData.question}`}
                                    answers={dimData.answers}
                                    pageNumber={currentPageNumber}
                                />
                            );
                        }
                    });

                    currentPageNumber += 1;
                    generatedPages.push(
                        <PageInterventionPlanSummarySelfcare360
                            key={`intervention-summary-${currentPageNumber}`}
                            individualResult={modifiedIndividualResult}
                            selectedEvaluation={selectedEvaluation}
                            orgLogo={orgLogo}
                            pageNumber={currentPageNumber}
                        />
                    );

                    detailedPlanRows.forEach((planRow) => {
                        currentPageNumber += 1;
                        generatedPages.push(
                            <PageInterventionPlanDimensionSelfcare360
                                key={`intervention-detail-${planRow.code}-${currentPageNumber}`}
                                individualResult={modifiedIndividualResult}
                                selectedEvaluation={selectedEvaluation}
                                orgLogo={orgLogo}
                                pageNumber={currentPageNumber}
                                planRow={planRow}
                            />
                        );
                    });

                    currentPageNumber += 1;
                    generatedPages.push(
                        <PageActionPlanSelfcare360
                            key={`action-plan-${currentPageNumber}`}
                            selectedEvaluation={selectedEvaluation}
                            pageNumber={currentPageNumber}
                            prefilledActions={actionPlanPrefillRows}
                        />
                    );

                    currentPageNumber += 1;
                    generatedPages.push(
                        <PageFarewellSelfcare360
                            key={`farewell-${currentPageNumber}`}
                            selectedEvaluation={selectedEvaluation}
                            pageNumber={currentPageNumber}
                        />
                    );

                    return generatedPages;
                })()}
            </div>

            <FloatingActionButtons
                handleDownloadPDF={handleDownloadPDF}
                handleOpenPDF={handleOpenPDF}
                handleExportExcel={() => console.log('Exportando a Excel...')}
                pdfLoading={pdfLoading}
                pdfProgress={pdfProgress}
            />
        </>
    );
};

export default Selfcare360Individual;
